// src/components/Carousel.js
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

const CarouselContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 500px;
  position: relative;
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  margin-bottom: 20px;
  width: 100%;
  max-width: 500px;
  position: relative;
  height: 60vh;
`;

const CardImage = styled.img`
  width: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: opacity 0.5s ease;
  opacity: ${props => props.active ? 1 : 0};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
`;

const NavControls = styled.div`
  position: absolute;
  display: flex;
  justify-content: space-between;
  width: 100%;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  padding: 0 10px;
  box-sizing: border-box;
`;

const NavButton = styled.button`
  background-color: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 1);
    transform: scale(1.1);
  }
`;

const Indicators = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
  position: absolute;
  bottom: -15px;
  width: 100%;
`;

const Indicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.active ? '#b38b6d' : 'rgba(179, 139, 109, 0.5)'};
  cursor: pointer;
  transition: all 0.3s ease;
  transform: ${props => props.active ? 'scale(1.2)' : 'scale(1)'};
`;

const Carousel = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const autoRotateRef = useRef(null);
  
  const goToNext = () => {
    setCurrentIndex(prev => (prev + 1) % images.length);
  };
  
  const goToPrev = () => {
    setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
  };
  
  const goToSlide = (index) => {
    setCurrentIndex(index);
  };
  
  const startAutoRotate = () => {
    autoRotateRef.current = setInterval(goToNext, 5000);
  };
  
  const stopAutoRotate = () => {
    if (autoRotateRef.current) {
      clearInterval(autoRotateRef.current);
    }
  };
  
  useEffect(() => {
    startAutoRotate();
    
    return () => stopAutoRotate();
  }, []);
  
  return (
    <CarouselContainer 
      onMouseEnter={stopAutoRotate}
      onMouseLeave={startAutoRotate}
    >
      <ImageContainer>
        {images.map((src, index) => (
          <CardImage 
            key={index}
            src={src} 
            alt={`Save The Date Card ${index + 1}`}
            active={index === currentIndex}
          />
        ))}
        
        <NavControls>
          <NavButton onClick={goToPrev}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#5a5a5a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </NavButton>
          <NavButton onClick={goToNext}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#5a5a5a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </NavButton>
        </NavControls>
        
        <Indicators>
          {images.map((_, index) => (
            <Indicator 
              key={index} 
              active={index === currentIndex}
              onClick={() => goToSlide(index)}
            />
          ))}
        </Indicators>
      </ImageContainer>
    </CarouselContainer>
  );
};

export default Carousel;